import React, { useCallback, useState, useEffect } from 'react';
import { useAdminStore, ApplicationStatus } from '../../../store/adminStore';
import { Input, Select, Button } from '../../../design-system';
import styles from './ApplicationFilters.module.css';

interface ApplicationFiltersProps {
  onSearch: (query: string) => void;
}

export const ApplicationFilters: React.FC<ApplicationFiltersProps> = ({ onSearch }) => {
  const {
    searchQuery,
    statusFilter,
    sortBy,
    sortOrder,
    setSearchQuery,
    setStatusFilter,
    setSortBy,
    setSortOrder,
    resetFilters,
  } = useAdminStore();

  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(localSearchQuery);
      onSearch(localSearchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [localSearchQuery, setSearchQuery, onSearch]);

  const handleStatusChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setStatusFilter(e.target.value as ApplicationStatus);
      onSearch(localSearchQuery);
    },
    [setStatusFilter, localSearchQuery, onSearch]
  );

  const handleSortChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setSortBy(e.target.value as any);
    },
    [setSortBy]
  );

  const handleSortOrderChange = useCallback(() => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  }, [sortOrder, setSortOrder]);

  const handleReset = useCallback(() => {
    setLocalSearchQuery('');
    resetFilters();
    onSearch('');
  }, [resetFilters, onSearch]);

  return (
    <div className={styles.container}>
      <div className={styles.filterGroup}>
        {/* Search Input */}
        <div className={styles.filterItem}>
          <label htmlFor="search" className={styles.label}>
            Search
          </label>
          <Input
            id="search"
            type="text"
            placeholder="Business name or email..."
            value={localSearchQuery}
            onChange={(e) => setLocalSearchQuery(e.target.value)}
            iconLeft={<SearchIcon />}
          />
        </div>

        {/* Status Filter */}
        <div className={styles.filterItem}>
          <label htmlFor="status" className={styles.label}>
            Status
          </label>
          <Select
            id="status"
            value={statusFilter}
            onChange={handleStatusChange}
            options={[
              { value: 'all', label: 'All Status' },
              { value: 'PENDING', label: 'Pending' },
              { value: 'APPROVED', label: 'Approved' },
              { value: 'REJECTED', label: 'Rejected' },
            ]}
          />
        </div>

        {/* Sort By */}
        <div className={styles.filterItem}>
          <label htmlFor="sort" className={styles.label}>
            Sort By
          </label>
          <Select
            id="sort"
            value={sortBy}
            onChange={handleSortChange}
            options={[
              { value: 'created_at', label: 'Date Submitted' },
              { value: 'store_name', label: 'Business Name' },
              { value: 'status', label: 'Status' },
            ]}
          />
        </div>

        {/* Sort Order */}
        <div className={styles.filterItem}>
          <label className={styles.label}>Order</label>
          <button
            className={styles.sortOrderButton}
            onClick={handleSortOrderChange}
            title={`Sort in ${sortOrder === 'asc' ? 'descending' : 'ascending'} order`}
            type="button"
          >
            {sortOrder === 'asc' ? '↑ Ascending' : '↓ Descending'}
          </button>
        </div>
      </div>

      {/* Reset Button */}
      <Button
        variant="secondary"
        size="sm"
        onClick={handleReset}
        type="button"
      >
        Reset Filters
      </Button>
    </div>
  );
};

// Search Icon Component
const SearchIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);
